import { motion } from "framer-motion";
import { Play, Lock } from "lucide-react";

export function VideoCard({ video, onOpen, index = 0, fullColor = false }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: (index % 5) * 0.05 }}
      className="group relative shrink-0 w-[300px] md:w-[360px] snap-start cursor-pointer"
      onClick={() => onOpen(video)}
      data-testid={`video-card-${video.id}`}
    >
      <div className="relative aspect-video overflow-hidden border border-white/10 bg-[#0A0A0A]">
        <img
          src={video.thumbnail}
          alt={video.title}
          className={`w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 ${
            fullColor ? "" : "grayscale group-hover:grayscale-0"
          }`}
        />
        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/50 transition-colors duration-500" />

        {/* badge */}
        <div className="absolute top-3 left-3">
          {video.ppv ? (
            <span className="flex items-center gap-1 bg-gold text-black text-[10px] font-bold uppercase tracking-widest px-2 py-1">
              {video.owned ? "Owned" : `PPV $${Number(video.ppv_price).toFixed(2)}`}
            </span>
          ) : video.is_premium ? (
            <span className="flex items-center gap-1 bg-white text-black text-[10px] font-semibold uppercase tracking-widest px-2 py-1">
              <Lock size={10} /> Premium
            </span>
          ) : (
            <span className="border border-white/60 text-white text-[10px] font-semibold uppercase tracking-widest px-2 py-1">
              Free
            </span>
          )}
        </div>

        {video.duration && (
          <span className="absolute bottom-3 right-3 font-mono-imn text-[11px] text-white/80 bg-black/60 px-2 py-0.5">
            {video.duration}
          </span>
        )}

        {/* play */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="backdrop-blur-md bg-white/20 border border-white/30 p-4 rounded-full">
            {video.locked ? <Lock size={22} /> : <Play size={22} fill="white" />}
          </div>
        </div>
      </div>

      <div className="pt-3">
        <h3 className="font-display text-lg font-medium uppercase tracking-tight leading-tight group-hover:text-gold transition-colors">
          {video.title}
        </h3>
        <p className="font-mono-imn text-[11px] tracking-widest text-white/40 uppercase mt-1">
          {video.category}
        </p>
      </div>
    </motion.div>
  );
}

export function VideoRow({ title, subtitle, videos, onOpen, fullColor = false }) {
  if (!videos?.length) return null;
  return (
    <section className="py-10">
      <div className="px-6 md:px-16 mb-6">
        {subtitle && (
          <p className="font-mono-imn text-xs tracking-[0.3em] text-gold uppercase mb-1">
            {subtitle}
          </p>
        )}
        <h2 className="font-display text-3xl md:text-4xl font-bold uppercase tracking-tight">
          {title}
        </h2>
      </div>
      <div className="flex gap-5 overflow-x-auto hide-scrollbar snap-x px-6 md:px-16 pb-4">
        {videos.map((v, i) => (
          <VideoCard key={v.id} video={v} onOpen={onOpen} index={i} fullColor={fullColor} />
        ))}
      </div>
    </section>
  );
}
